import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaSpinner,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
} from "react-icons/fa";
import { API_URL } from "../config/apiConfig";
import axios from "axios";

const SocialMediaIconMap = {
  facebook: FaFacebook,
  twitter: FaTwitter,
  instagram: FaInstagram,
  linkedin: FaLinkedin,
};

const SupportDetails = () => {
  const navigate = useNavigate();
  const [support, setSupport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSupportDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/content/public/support-details`);
        if (response.data.success) {
          setSupport(response.data.data);
        } else {
          setError("Failed to load support details");
        }
      } catch (err) {
        console.error("Error fetching support details:", err);
        setError("Unable to load support details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchSupportDetails();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-primary text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading support details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back Button */}
        <div className="mb-3 md:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">
            {support?.companyName || "Support & Contact"} Support
          </h1>
          <p className="text-gray-600 text-lg">
            {support?.description || "Get in touch with our support team"}
          </p>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-700">{error}</p>
          </div>
        ) : support ? (
          <div className="space-y-4 md:space-y-6">
            {/* Contact Channels */}
            {support.contactChannels && support.contactChannels.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
                <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">
                  Contact Channels
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {support.contactChannels.map((channel, index) => {
                    let Icon = FaPhone;
                    if (channel.type === "email") Icon = FaEnvelope;
                    else if (channel.type === "phone") Icon = FaPhone;

                    return (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 md:p-6 hover:border-primary transition-colors"
                      >
                        <div className="flex items-start">
                          <Icon className="text-primary text-xl mr-4 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-800 capitalize mb-1">
                              {channel.type || "Contact"}
                            </h3>
                            <p className="text-gray-600">
                              {channel.value || channel.name}
                            </p>
                            {channel.description && (
                              <p className="text-sm text-gray-500 mt-2">
                                {channel.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Working Hours */}
            {support.workingHours && support.workingHours.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
                <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center">
                  <FaClock className="mr-3 text-primary" />
                  Working Hours
                </h2>
                <div className="space-y-3">
                  {support.workingHours.map((hours, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="font-semibold text-gray-800 capitalize">
                        {hours.day || `Day ${index + 1}`}
                      </span>
                      <span className="text-gray-600">
                        {hours.open} - {hours.close}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Office Locations */}
            {support.officeLocations && support.officeLocations.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
                <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center">
                  <FaMapMarkerAlt className="mr-3 text-primary" />
                  Office Locations
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {support.officeLocations.map((location, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 md:p-6 hover:border-primary transition-colors"
                    >
                      <h3 className="font-semibold text-gray-800 mb-3">
                        {location.city || location.name}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {location.address}
                      </p>
                      {location.phone && (
                        <p className="text-sm text-gray-600 mt-3">
                          <strong>Phone:</strong> {location.phone}
                        </p>
                      )}
                      {location.email && (
                        <p className="text-sm text-gray-600">
                          <strong>Email:</strong> {location.email}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Emergency Contact */}
            {support.emergencyContact && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-5 md:p-8">
                <h2 className="text-lg md:text-2xl font-bold text-red-800 mb-2">
                  Emergency Support
                </h2>
                <p className="text-red-700 mb-2">
                  {support.emergencyContact.message || "For urgent assistance:"}
                </p>
                <p className="text-lg font-semibold text-red-800">
                  {support.emergencyContact.phone || support.emergencyContact.value}
                </p>
                <p className="text-sm text-red-600 mt-2">
                  Available 24/7
                </p>
              </div>
            )}

            {/* Social Media */}
            {support.socialMedia && support.socialMedia.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
                <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">
                  Follow Us
                </h2>
                <div className="flex gap-4 flex-wrap">
                  {support.socialMedia.map((social, index) => {
                    const Icon = SocialMediaIconMap[social.platform] || FaPhone;
                    return (
                      <a
                        key={index}
                        href={social.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-primary hover:text-white transition-colors"
                      >
                        <Icon />
                        <span className="capitalize">{social.platform}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Website */}
            {support.website && (
              <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Website</h3>
                <a
                  href={support.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark underline"
                >
                  {support.website}
                </a>
              </div>
            )}
          </div>
        ) : null}

        {/* Related Links */}
        <div className="mt-6 md:mt-12 pt-4 md:pt-8 border-t border-gray-200 text-center">
          <div className="inline-flex space-x-4">
            <Link
              to="/faqs"
              className="text-primary hover:text-primary-dark underline"
            >
              FAQs
            </Link>
            <Link
              to="/how-to-use"
              className="text-primary hover:text-primary-dark underline"
            >
              How to Use
            </Link>
            <Link
              to="/cancellation-policy"
              className="text-primary hover:text-primary-dark underline"
            >
              Cancellation Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportDetails;
